import React, { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  Select,
  Input,
  Button,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  message,
  Tooltip,
  Switch,
  Alert,
  InputNumber,
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  RedoOutlined,
  DownloadOutlined,
  CopyOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { paymentApi, retryOrderApi } from '../services/api';
import type { ColumnsType } from 'antd/es/table';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title, Text } = Typography;
const { Option } = Select;

const copyText = async (value?: string | null) => {
  if (!value) {
    message.warning('暂无可复制内容');
    return;
  }

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
    message.success('已复制');
  } catch {
    message.error('复制失败，请手动复制');
  }
};

const CopyableCode: React.FC<{ value?: string | null; fontSize?: number }> = ({
  value,
  fontSize = 12,
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    }}
  >
    <Text code style={{ fontSize, margin: 0 }}>
      {value || '-'}
    </Text>
    {value && (
      <Tooltip title="复制">
        <Button
          type="text"
          size="small"
          aria-label="复制"
          icon={<CopyOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            copyText(value);
          }}
          style={{
            flex: '0 0 24px',
            width: 24,
            height: 24,
            minWidth: 24,
            padding: 0,
            lineHeight: '24px',
          }}
        />
      </Tooltip>
    )}
  </span>
);

interface Payment {
  payment_id: string;
  order_id: number;
  order_number?: string;
  amount: number;
  actual_paid_amount?: number | null;
  currency: string;
  payment_method: string;
  payment_address: string;
  status: string;
  expires_at: string;
  created_at: string;
  tx_hash?: string;
  confirmations: number;
  paid_at?: string;
  confirmed_at?: string;
  error_message?: string;
  user_id: number;
  username?: string;
  first_name?: string;
  bot_id?: number;
  bot_username?: string;
  bot_name?: string;
  product_name?: string;
  product_type?: string;
  merchant_username?: string;
}

const PaymentManagement: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [botFilter, setBotFilter] = useState<number | undefined>(undefined);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);

  // 退款状态
  const [refunding, setRefunding] = useState(false);

  // 补单相关状态
  const [retryOrderNumber, setRetryOrderNumber] = useState('');
  const [retryActualAmount, setRetryActualAmount] = useState<number | null>(null);
  const [retryForce, setRetryForce] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [retryResult, setRetryResult] = useState<{ success: boolean; message: string; epay_status?: string } | null>(null);

  // 获取支付列表
  const { data: paymentsData, isLoading, refetch: refetchPayments } = useQuery(
    ['payments', statusFilter, botFilter, paymentMethodFilter, searchText, page, pageSize],
    () =>
      paymentApi.getPayments({
        status: statusFilter,
        bot_id: botFilter,
        payment_method: paymentMethodFilter,
        search: searchText || undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      }),
    {
      keepPreviousData: true,
    }
  );

  // 获取统计数据（使用专用统计API，支持实时更新）
  const { data: statsData } = useQuery({
    queryKey: ['payment-stats', botFilter],
    queryFn: () => paymentApi.getStats({ bot_id: botFilter }),
    refetchInterval: 30000, // 每30秒自动刷新，实现实时统计
  });

  // 获取机器人列表用于筛选
  const { data: botsData } = useQuery({
    queryKey: ['payment-bots'],
    queryFn: paymentApi.getBots,
  });

  // 获取待补单订单列表
  const { data: pendingOrdersData, refetch: refetchPendingOrders } = useQuery({
    queryKey: ['pending-retry-orders'],
    queryFn: () => retryOrderApi.getPendingOrders(50),
  });

  const pendingRetryOrders = (pendingOrdersData as any)?.orders || [];

  const payments = (paymentsData as any)?.items || [];
  const total = (paymentsData as any)?.total || 0;
  const bots = (botsData as any) || [];

  // 从统计API获取的数据（API直接返回统计对象，无需解包data字段）
  const statsInfo = (statsData as any) || {};

  // 统计卡片数据（使用实时统计API）
  const stats = {
    total: statsInfo.total_count || 0,
    completed: statsInfo.success_count || 0,
    pending: statsInfo.pending_count || 0,
    failed: (statsInfo.failed_count || 0) + (statsInfo.other_count || 0),
    // “总收入”口径：只统计实际支付成功（confirmed/completed），不包含待支付等状态
    totalAmount: statsInfo.success_amount ?? statsInfo.total_amount ?? 0,
  };

  // 状态标签映射
  const statusMap: Record<string, { color: string; text: string; icon: any }> = {
    pending: { color: 'processing', text: '待支付', icon: <ClockCircleOutlined /> },
    processing: { color: 'processing', text: '处理中', icon: <ClockCircleOutlined /> },
    confirming: { color: 'warning', text: '确认中', icon: <ClockCircleOutlined /> },
    confirmed: { color: 'success', text: '已确认', icon: <CheckCircleOutlined /> },
    completed: { color: 'success', text: '已完成', icon: <CheckCircleOutlined /> },
    failed: { color: 'error', text: '失败', icon: <CloseCircleOutlined /> },
    expired: { color: 'default', text: '已过期', icon: <CloseCircleOutlined /> },
    refunded: { color: 'warning', text: '已退款', icon: <CloseCircleOutlined /> },
  };

  // 支付方式映射
  const paymentMethodMap: Record<string, string> = {
    usdt_trc20: 'USDT (TRC20)',
    usdt_erc20: 'USDT (ERC20)',
    usdt_omni: 'USDT (Omni)',
    usdt: 'USDT',
    alipay_1: '支付宝1(旧)',
    alipay_2: '支付宝①同盟',
    alipay_3: '支付宝②好运来',
    alipay_4: '支付宝③代帝',
    alipay_5: '支付宝④黑猫',
    alipay_6: '支付宝⑤泡泡',
    wechat_2: '微信②好运来',
    wechat_1: '微信①同盟',
    wechat_3: '微信④黑猫',
    wechat_4: '微信⑤泡泡',
    wechat_5: '微信③代帝',
    guoji: '国际代付',
    alipay: '支付宝',
    wxpay: '微信支付',
    wechat_pay: '微信支付',
    minyue_alipay: '支付宝(敏悦)',
    minyue_wxpay: '微信支付(敏悦)',
  };

  const paymentMethodOptions: string[] = [
    'usdt',
    'alipay_2',
    'alipay_3',
    'alipay_4',
    'alipay_6',
    'wechat_1',
    'wechat_3',
    'wechat_4',
    'wechat_5',
    'guoji',
    // 历史通道（已停用，保留供查旧单）
    'alipay_1',
    'alipay_5',
    'wechat_2',
  ];

  const isUsdtPayment = (paymentMethod?: string) =>
    paymentMethod?.toLowerCase().startsWith('usdt') ?? false;

  const handleViewDetail = (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailModalVisible(true);
  };

  // 退款操作
  const handleRefund = (record: Payment) => {
    let banUser = false;
    const isUSDT = isUsdtPayment(record.payment_method);
    const amountText = isUSDT
      ? `${record.amount.toFixed(2)} USDT`
      : `¥${record.amount.toFixed(2)}`;
    const merchantText = record.merchant_username
      ? `@${record.merchant_username}`
      : '未知';

    Modal.confirm({
      title: '确认退款？',
      content: (
        <div>
          <p>订单金额：<strong>{amountText}</strong></p>
          <p>商户：<strong>{merchantText}</strong></p>
          <p style={{ color: '#faad14', marginTop: 8 }}>
            退款将从商户余额中扣减该金额。
          </p>
          <div style={{ marginTop: 12 }}>
            <Space>
              <Switch
                checkedChildren="拉黑"
                unCheckedChildren="不拉黑"
                defaultChecked={false}
                onChange={(checked) => {
                  banUser = checked;
                }}
              />
              <Text type="secondary">买重/补偿退款请保持“不拉黑”</Text>
            </Space>
          </div>
        </div>
      ),
      okText: '确认退款',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setRefunding(true);
        try {
          await paymentApi.quickRefund(record.payment_id, undefined, banUser);
          message.success('退款成功');
          refetchPayments();
        } catch (error: any) {
          const detail = error.response?.data?.detail || '退款失败';
          message.error(detail);
        } finally {
          setRefunding(false);
        }
      },
    });
  };

  const canResendInvite = (record: Payment) =>
    record.product_type === 'group'
    && Boolean(record.order_number)
    && ['confirmed', 'completed', 'paid'].includes(record.status);

  // 补单操作
  const handleRetryOrder = async (orderNumber?: string, force?: boolean, actualAmount?: number | null) => {
    const num = orderNumber || retryOrderNumber.trim();
    if (!num) {
      message.warning('请输入订单号');
      return;
    }
    const amount = actualAmount ?? retryActualAmount;
    if (amount !== null && amount !== undefined && amount <= 0) {
      message.warning('实际到账金额必须大于 0');
      return;
    }
    setRetrying(true);
    setRetryResult(null);
    try {
      const result = await retryOrderApi.retry({
        order_number: num,
        force: force ?? retryForce,
        actual_amount: amount || undefined,
      }) as any;

      // 支付平台确认未支付，需要管理员二次确认
      if (result.needs_confirm) {
        setRetryResult({
          success: false,
          message: result.message,
          epay_status: result.epay_status,
        });
        Modal.confirm({
          title: '支付平台显示未支付',
          content: `${result.epay_status || '未知状态'}\n\n确认要强制补单吗？这将直接发放商品给用户。`,
          okText: '确认强制补单',
          okType: 'danger',
          cancelText: '取消',
          onOk: () => handleRetryOrder(num, true, amount),
        });
        return;
      }

      setRetryResult({
        success: result.success,
        message: result.message,
        epay_status: result.epay_status,
      });
      if (result.success) {
        message.success(result.message);
        if (amount) {
          setRetryActualAmount(null);
        }
        refetchPayments();
        refetchPendingOrders();
      } else {
        message.error(result.message);
      }
    } catch (error: any) {
      const detail = error.response?.data?.detail || '补单失败';
      setRetryResult({ success: false, message: detail });
      message.error(detail);
    } finally {
      setRetrying(false);
    }
  };

  const handleResendInvite = async (orderNumber?: string) => {
    const num = orderNumber || retryOrderNumber.trim();
    if (!num) {
      message.warning('请输入订单号');
      return;
    }

    Modal.confirm({
      title: '确认重发进群链接',
      content: `订单号：${num}\n\n该操作只会重新生成并发送进群链接，不会重复入账或延长会员时长。`,
      okText: '确认重发',
      cancelText: '取消',
      onOk: async () => {
        setResendingInvite(true);
        setRetryResult(null);
        try {
          const result = await retryOrderApi.resendInvite({ order_number: num }) as any;
          setRetryResult({
            success: result.success,
            message: result.message,
            epay_status: result.epay_status,
          });
          if (result.success) {
            message.success(result.message);
          } else {
            message.error(result.message || '重发链接失败');
          }
        } catch (error: any) {
          const detail = error.response?.data?.detail || '重发链接失败';
          setRetryResult({ success: false, message: detail });
          message.error(detail);
        } finally {
          setResendingInvite(false);
        }
      },
    });
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result: any = await paymentApi.syncPayments();
      const updated = result?.updated ?? 0;
      const skipped = result?.skipped ?? 0;
      const errors = result?.errors ?? 0;
      const usdtProcessed = result?.usdt_processed ?? 0;
      const usdtText = usdtProcessed ? `，USDT确认${usdtProcessed}笔` : '';
      message.success(`同步完成：更新${updated}条，跳过${skipped}条，错误${errors}条${usdtText}`);
    } catch (error) {
      message.error('同步失败，请稍后重试');
    } finally {
      setSyncing(false);
      refetchPayments();
    }
  };

  const getDownloadFilename = (contentDisposition?: string) => {
    if (!contentDisposition) {
      return `payments_export_${Date.now()}.xlsx`;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const defaultMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return defaultMatch?.[1] || `payments_export_${Date.now()}.xlsx`;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await paymentApi.exportPayments({
        status: statusFilter,
        bot_id: botFilter,
        payment_method: paymentMethodFilter,
        search: searchText || undefined,
      });
      const filename = getDownloadFilename(response.headers['content-disposition']);
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<Payment> = [
    {
      title: '订单号',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 190,
      render: (text: string) => <CopyableCode value={text} />,
    },
    {
      title: '用户',
      key: 'user',
      width: 150,
      render: (_: any, record: Payment) => (
        <div>
          <div className="font-medium">
            {record.username ? `@${record.username}` : record.first_name || '-'}
          </div>
          <div className="text-xs text-gray-500">ID: {record.user_id}</div>
        </div>
      ),
    },
    {
      title: '所属机器人',
      key: 'bot',
      width: 180,
      render: (_: any, record: Payment) => (
        <div>
          {record.bot_username ? (
            <>
              <div className="font-medium text-blue-600">@{record.bot_username}</div>
              <div className="text-xs text-gray-500">{record.bot_name || '-'}</div>
            </>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </div>
      ),
    },
    {
      title: '商品名称',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 150,
      render: (text: string) => text || <Text type="secondary">-</Text>,
    },
    {
      title: '商户',
      dataIndex: 'merchant_username',
      key: 'merchant_username',
      width: 120,
      render: (text: string) => text ? <Text className="text-blue-600">@{text}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount: number, record: Payment) => {
        const isUSDT = isUsdtPayment(record.payment_method);
        return (
          <Text strong className="text-green-600">
            {isUSDT ? '' : '¥'}{amount.toFixed(2)} {isUSDT ? 'USDT' : ''}
          </Text>
        );
      },
    },
    {
      title: '实付金额',
      dataIndex: 'actual_paid_amount',
      key: 'actual_paid_amount',
      width: 120,
      align: 'right',
      render: (actualPaidAmount: number | null | undefined, record: Payment) => {
        const isUSDT = isUsdtPayment(record.payment_method);
        const displayAmount = actualPaidAmount ?? record.amount;
        return (
          <Text>
            {isUSDT ? '' : '¥'}{Number(displayAmount || 0).toFixed(2)} {isUSDT ? 'USDT' : ''}
          </Text>
        );
      },
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 130,
      render: (method: string) => paymentMethodMap[method] || method,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusInfo = statusMap[status] || { color: 'default', text: status, icon: null };
        return (
          <Tag color={statusInfo.color} icon={statusInfo.icon}>
            {statusInfo.text}
          </Tag>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => formatBeijingTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      render: (_: any, record: Payment) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {canResendInvite(record) && (
            <Button
              type="text"
              size="small"
              icon={<SendOutlined />}
              loading={resendingInvite}
              onClick={() => handleResendInvite(record.order_number)}
            >
              重发链接
            </Button>
          )}
          {(record.status === 'confirmed' || record.status === 'completed') && (
            <Button
              type="text"
              danger
              size="small"
              loading={refunding}
              onClick={() => handleRefund(record)}
            >
              退款
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <Title level={2} className="mb-2">
          订单管理
        </Title>
        <Text type="secondary">管理和查看所有订单</Text>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总支付订单"
              value={stats.total}
              prefix={<DollarOutlined className="text-blue-500" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已完成"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="待支付"
              value={stats.pending}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总收入"
              value={stats.totalAmount}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
              suffix="综合"
            />
          </Card>
        </Col>
      </Row>

      {/* 手动补单 */}
      <Card title="手动补单" size="small">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="输入订单号（如 ORD-xxxx）"
                value={retryOrderNumber}
                onChange={(e) => setRetryOrderNumber(e.target.value)}
                onPressEnter={() => handleRetryOrder()}
                allowClear
              />
              <InputNumber
                min={0}
                precision={2}
                value={retryActualAmount}
                onChange={(value) => setRetryActualAmount(value)}
                placeholder="实际到账金额"
                style={{ width: 160 }}
              />
            </Space.Compact>
          </Col>
          <Col>
            <Space>
              <Tooltip title="填写后会按实际到账金额补单，并按比例调整商户入账和分销金额">
                <Text type={retryActualAmount ? 'warning' : 'secondary'}>
                  {retryActualAmount ? '按实际金额补单' : '原金额补单'}
                </Text>
              </Tooltip>
              <Tooltip title="跳过支付平台验证，强制执行补单">
                <Switch
                  checkedChildren="强制"
                  unCheckedChildren="普通"
                  checked={retryForce}
                  onChange={setRetryForce}
                />
              </Tooltip>
              <Button
                type="primary"
                icon={<RedoOutlined />}
                loading={retrying}
                onClick={() => handleRetryOrder()}
              >
                查询并补单
              </Button>
              <Button
                icon={<SendOutlined />}
                loading={resendingInvite}
                onClick={() => handleResendInvite()}
              >
                重发链接
              </Button>
            </Space>
          </Col>
        </Row>
        {retryResult && (
          <Alert
            className="mt-3"
            type={retryResult.success ? 'success' : 'error'}
            showIcon
            message={retryResult.message}
            description={retryResult.epay_status ? `支付平台状态：${retryResult.epay_status}` : undefined}
            closable
            onClose={() => setRetryResult(null)}
          />
        )}

        {/* 待补单订单列表 */}
        {pendingRetryOrders.length > 0 && (
          <div className="mt-4">
            <Typography.Text strong>待补单订单（未完成）：{pendingRetryOrders.length} 笔</Typography.Text>
            <Table
              className="mt-2"
              size="small"
              dataSource={pendingRetryOrders}
              rowKey="order_number"
              pagination={false}
              scroll={{ y: 240 }}
              columns={[
                {
                  title: '订单号',
                  dataIndex: 'order_number',
                  width: 200,
                  render: (text: string) => <CopyableCode value={text} />,
                },
                {
                  title: '金额',
                  dataIndex: 'amount',
                  width: 100,
                  render: (v: number) => <Text strong className="text-green-600">{v?.toFixed(2)}</Text>,
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 80,
                  render: (s: string) => {
                    const map: Record<string, { color: string; text: string }> = {
                      paid: { color: 'warning', text: '已支付' },
                      pending: { color: 'processing', text: '待支付' },
                      cancelled: { color: 'default', text: '已取消' },
                      expired: { color: 'default', text: '已过期' },
                    };
                    const info = map[s] || { color: 'default', text: s };
                    return <Tag color={info.color}>{info.text}</Tag>;
                  },
                },
                {
                  title: '商品',
                  dataIndex: 'product_name',
                  width: 150,
                  render: (text: string) => text || '-',
                },
                {
                  title: '创建时间',
                  dataIndex: 'created_at',
                  width: 160,
                  render: (date: string) => date ? formatBeijingTime(date) : '-',
                },
                {
                  title: '操作',
                  width: 100,
                  render: (_: any, record: any) => (
                    <Button
                      type="link"
                      size="small"
                      icon={<RedoOutlined />}
                      loading={retrying}
                      onClick={() => handleRetryOrder(record.order_number, false, null)}
                    >
                      补单
                    </Button>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Card>

      {/* 筛选和搜索 */}
      <Card>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <Select
              placeholder="选择机器人"
              allowClear
              style={{ width: 200 }}
              value={botFilter}
              onChange={setBotFilter}
            >
              {bots.map((bot: any) => (
                <Option key={bot.id} value={bot.id}>
                  @{bot.bot_username}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="选择状态"
              allowClear
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="completed">已完成</Option>
              <Option value="expired">已过期</Option>
            </Select>

            <Select
              placeholder="支付方式"
              allowClear
              style={{ width: 170 }}
              value={paymentMethodFilter}
              onChange={(value) => {
                setPaymentMethodFilter(value);
                setPage(1);
              }}
            >
              {paymentMethodOptions.map((method) => (
                <Option key={method} value={method}>
                  {paymentMethodMap[method] || method}
                </Option>
              ))}
            </Select>

            <Input.Search
              placeholder="搜索单号、买家、商户、商品、机器人"
              allowClear
              style={{ width: 260 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => setPage(1)}
            />

            <Button icon={<ReloadOutlined />} onClick={handleSync} loading={syncing}>
              刷新
            </Button>

            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              导出 Excel
            </Button>
          </div>

          {/* 支付列表表格 */}
          <Table
            columns={columns}
            dataSource={payments}
            rowKey="payment_id"
            loading={isLoading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page, pageSize) => {
                setPage(page);
                setPageSize(pageSize);
              },
            }}
            scroll={{ x: 1780 }}
          />
        </div>
      </Card>

      {/* 支付详情弹窗 */}
      <Modal
        title="支付详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {selectedPayment && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="订单号" span={2}>
              <CopyableCode value={selectedPayment.order_number} />
            </Descriptions.Item>
            <Descriptions.Item label="订单ID">
              {selectedPayment.order_id}
            </Descriptions.Item>
            <Descriptions.Item label="用户">
              {selectedPayment.username
                ? `@${selectedPayment.username}`
                : selectedPayment.first_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="所属机器人" span={2}>
              {selectedPayment.bot_username ? (
                <div>
                  <div className="font-medium text-blue-600">
                    @{selectedPayment.bot_username}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedPayment.bot_name || '-'}
                  </div>
                </div>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="商品名称" span={2}>
              {selectedPayment.product_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="商品类型">
              {selectedPayment.product_type === 'group' ? '会员群' : selectedPayment.product_type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="金额">
              {isUsdtPayment(selectedPayment.payment_method)
                ? `${selectedPayment.amount.toFixed(2)} USDT`
                : `¥${selectedPayment.amount.toFixed(2)}`}
            </Descriptions.Item>
            <Descriptions.Item label="支付方式">
              {paymentMethodMap[selectedPayment.payment_method] ||
                selectedPayment.payment_method}
            </Descriptions.Item>
            <Descriptions.Item label="支付地址" span={2}>
              <CopyableCode value={selectedPayment.payment_address} />
            </Descriptions.Item>
            {selectedPayment.tx_hash && (
              <Descriptions.Item label="交易哈希" span={2}>
                <CopyableCode value={selectedPayment.tx_hash} />
              </Descriptions.Item>
            )}
            <Descriptions.Item label="确认数">
              {selectedPayment.confirmations}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {statusMap[selectedPayment.status] && (
                <Tag
                  color={statusMap[selectedPayment.status].color}
                  icon={statusMap[selectedPayment.status].icon}
                >
                  {statusMap[selectedPayment.status].text}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间" span={2}>
              {formatBeijingTime(selectedPayment.created_at)}
            </Descriptions.Item>
            {selectedPayment.paid_at && (
              <Descriptions.Item label="支付时间" span={2}>
                {formatBeijingTime(selectedPayment.paid_at)}
              </Descriptions.Item>
            )}
            {selectedPayment.confirmed_at && (
              <Descriptions.Item label="确认时间" span={2}>
                {formatBeijingTime(selectedPayment.confirmed_at)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="过期时间" span={2}>
              {formatBeijingTime(selectedPayment.expires_at)}
            </Descriptions.Item>
            {selectedPayment.error_message && (
              <Descriptions.Item label="错误信息" span={2}>
                <Text type="danger">{selectedPayment.error_message}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default PaymentManagement;
